import config from '../config.js';
import { canonicalizeLocale } from '../locales/index.js';

/**
 * Frontend side of the authenticated locale preference (ENG-379).
 *
 * A signed-in listener's language choice belongs to the account, so it
 * follows them to any browser they sign in on; `localStorage` (ENG-387) stays
 * the device's copy for anonymous visitors, for the moment before the account
 * answers, and as a mirror of the last account value so the next load starts
 * in the right language without a flash. Everything the frontend expects of
 * the backend is pinned here for it to diff against:
 *
 * Endpoints (authenticated, under `config.apiBaseUrl`):
 *   GET /me/locale-preference
 *       → { locale: string | null }
 *       The BCP 47 tag the listener last chose on any device, null when they
 *       never chose one. The client applies it only when this build can
 *       activate it (exact tag, else the same language — `en-GB` → `en-US`);
 *       a tag it cannot offer leaves the device's locale alone rather than
 *       falling back to English over an explicit local choice.
 *   PUT /me/locale-preference
 *       ← { locale: string }        canonical tag of a locale offered by this build
 *       → { locale } or 204         the saved state; the client does not re-read it
 *   404 / 501 → the backend has not shipped this: the device copy stands alone.
 *   401 / 403 → reported as unauthorized; the path is exempt from
 *       fetchWrapper's auto-logout because it is requested on every signed-in
 *       page load, so a refusal there must not end the session.
 *
 * Nothing is written to the account until the listener chooses a language
 * while signed in: an account with `locale: null` is not seeded from the
 * device, and a device is never told what the account did not say.
 */
export const LOCALE_PREFERENCE_PATH = '/me/locale-preference';

export const localePreferenceEndpoint = () => `${config.apiBaseUrl}${LOCALE_PREFERENCE_PATH}`;

/**
 * Where the account preference stands, as `useLocale().preference.status`.
 * `idle` is a signed-out visitor (the device copy alone); `ready` means the
 * account answered and choices made here are saved to it; `error` is a load
 * that failed for a reason other than the two below, after which a choice is
 * still offered to the account; `unavailable` and `unauthorized` mean it
 * will not take one, so choices stay on the device.
 */
export const LOCALE_PREFERENCE_STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
  UNAVAILABLE: 'unavailable',
  UNAUTHORIZED: 'unauthorized',
});

/** What consumers see without a `LocalePreferenceSync` (tests, isolated mounts): the device copy alone. */
export const DEVICE_ONLY_PREFERENCE = Object.freeze({
  status: LOCALE_PREFERENCE_STATUS.IDLE,
  saving: false,
  saveError: null,
});

/**
 * The canonical tag a GET (or PUT) response names, or null when it names
 * none: `{ locale: null }`, an empty 204, or a value that is not a BCP 47
 * tag. Accepts a bare string too, so a backend that answers with just the
 * tag still reads.
 */
export const readPreferredLocale = (response) => {
  const raw = typeof response === 'string' ? response : response?.locale;
  return canonicalizeLocale(raw);
};

export const serializeLocalePreference = (locale) => ({ locale });

/** 404/501 from fetchWrapper: the endpoint does not exist on this deployment. */
export const isLocalePreferenceUnavailableError = (error) => [404, 501].includes(Number(error?.status));

/** 401/403 from fetchWrapper; the path is exempt from its auto-logout, so the caller sees these. */
export const isLocalePreferenceUnauthorizedError = (error) => [401, 403].includes(Number(error?.status));
